import EmailIcon from '@mui/icons-material/MailOutline';
import {Avatar, Button} from '@mui/material';
import Box from '@mui/material/Box';
import MUILink from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import {array, string} from 'prop-types';
import React, {useRef, useState} from 'react';
import {getNewSearchUrl} from '../utils.js';
import Link from './Link.jsx';

function Author({name, email, location, affiliation}) {
  const authorSearchUrl = getNewSearchUrl({query: `"${name}"`});

  const affiliationSearchUrl = getNewSearchUrl({query: `"${affiliation}"`});

  const [anchorEl, setAnchorEl] = useState(null);

  const popoverTargetRef = useRef();

  const handleToggleOn = () => {
    setAnchorEl(popoverTargetRef.current);
  };

  const handleToggleOff = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  return (
    <Box
      display="contents"
      onMouseOver={handleToggleOn}
      onMouseLeave={handleToggleOff}
      onMouseOut={handleToggleOff}
    >
      <Popper open={open} anchorEl={anchorEl}>
        <Paper
          variant="outlined"
          sx={{
            padding: 2,
            marginTop: 1,
            marginRight: 2,
            marginBottom: 2,
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            maxWidth: 512,
          }}
        >
          <Box flexDirection="row" display="flex" alignItems="center" gap={1}>
            <Avatar />
            <Typography>{name}</Typography>
          </Box>
          <Box sx={{textAlign: 'center'}}>
            <Typography variant="caption" component="div">
              {affiliation && (
                <Link to={affiliationSearchUrl}>{affiliation}</Link>
              )}
            </Typography>
            <Typography variant="caption">{location}</Typography>
          </Box>
          <Box
            flexDirection="row"
            display="flex"
            gap={1}
            flexWrap="wrap"
            justifyContent="center"
          >
            <Button size="small" variant="outlined">
              <Typography>SciProfile</Typography>
            </Button>

            <Button
              size="small"
              variant="outlined"
              href={`https://scilit.com/scholars?q=${name}`}
              target="_blank"
            >
              <Typography>Scilit</Typography>
            </Button>
            <Button
              size="small"
              variant="outlined"
              href={`https://www.preprints.org/search?search1=${name}&field1=authors`}
              target="_blank"
            >
              <Typography>Preprints.org</Typography>
            </Button>
            <Button
              size="small"
              variant="outlined"
              href={`https://scholar.google.com/scholar?q=${name}`}
              target="_blank"
            >
              <Typography>Google Scholar</Typography>
            </Button>
          </Box>
        </Paper>
      </Popper>
      <Paper
        ref={popoverTargetRef}
        sx={{
          marginRight: 2,
          boxShadow: 'none',
          background: 'none',
        }}
      >
        <Typography>
          <Link to={authorSearchUrl} sx={{fontSize: '0.875rem'}}>
            {name}
          </Link>
          {email && (
            <>
              &nbsp;
              <Tooltip title="email author">
                <MUILink href={`mailto:${email}`} target="_blank">
                  <EmailIcon sx={{fontSize: '0.875rem'}} />
                </MUILink>
              </Tooltip>
            </>
          )}
        </Typography>
      </Paper>
    </Box>
  );
}

Author.propTypes = {
  name: string.isRequired,
  email: string,
  affiliation: string.isRequired,
  location: string.isRequired,
};

function PublicationAuthors({authors}) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        alignItems: 'center',
      }}
    >
      <Typography mr={1} sx={{fontSize: '0.875rem'}}>
        By:
      </Typography>
      {authors.map(author => (
        <Author key={author.name} {...author} />
      ))}
    </Box>
  );
}

PublicationAuthors.propTypes = {
  authors: array.isRequired,
};

export default PublicationAuthors;
